import {
  ApolloClient,
  OnDataOptions,
  useApolloClient,
  useQuery,
  useSubscription,
} from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Queries, Subscriptions } from '@shared/api';
import { Field } from '@shared/data/entity/fields/Field';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { API } from '@shared/types/api';
import { User } from '@shared/types/user';
import { useProfile } from '@shared/util/hooks';
import { FieldRecord, GetUpdatedField, UpdateField } from '../../types';
import { buildFieldRecord } from '../../util/buildFieldRecord';
import { getModifiedFields, isFieldModified } from '../../util/isFieldRecordModified';

export interface EditFieldsHook {
  fields: FieldRecord;
  rawFields?: API.WorkflowEntity['fields'] | null;
  originalFields: FieldRecord | null;
  modifiedFields: FieldRecord | null;
  threadId?: string;
  errors: CardEntityError[];
  loading: boolean;
  isDirty: boolean;
  updateField: UpdateField;
  resetDirty: () => void;
  conflictingComponentIds: string[];
  revertConflicts: () => void;
  /**
   * Marks component IDs as having a save in flight, so a subscription event for
   * one of them is treated as our own change echoing back rather than a remote conflict.
   */
  markComponentsPendingSave: (componentIds: string[]) => void;
  /** Clears the pending-save marker once the save request has settled. */
  clearComponentsPendingSave: (componentIds: string[]) => void;
}

export function useEditFields(
  entityId: string,
  workflowTemplate?: API.WorkflowTemplateFragment,
): EditFieldsHook {
  const { data, loading, error } = useQuery<
    API.GetWorkflowEntityQuery,
    API.GetWorkflowEntityQueryVariables
  >(Queries.workflow.getWorkflowEntity, {
    variables: { input: { id: entityId } },
    skip: isNil(entityId),
  });
  const { profile } = useProfile();
  const client = useApolloClient();
  const [originalFields, setOriginalFields] = useState<FieldRecord | null>(null);
  const [fields, setFields] = useState<FieldRecord>({});
  const [conflictingComponentIds, setConflictingComponentIds] = useState<string[]>([]);
  const [conflictingRemoteFields, setConflictingRemoteFields] = useState<FieldRecord>({});
  const pendingSaveComponentIdsRef = useRef<Set<string>>(new Set());

  const markComponentsPendingSave = useCallback((componentIds: string[]) => {
    componentIds.forEach(id => pendingSaveComponentIdsRef.current.add(id));
  }, []);

  const clearComponentsPendingSave = useCallback((componentIds: string[]) => {
    componentIds.forEach(id => pendingSaveComponentIdsRef.current.delete(id));
  }, []);
  const modifiedFields = useMemo(
    () => getModifiedFields(originalFields, fields),
    [originalFields, fields],
  );

  useEffect(() => {
    if (entityId) {
      setFields({});
      setOriginalFields(null);
      setConflictingComponentIds([]);
      setConflictingRemoteFields({});
    }
  }, [entityId]);

  const isDirty = !isNil(modifiedFields) && Object.keys(modifiedFields).length > 0;

  useSubscription<API.WorkflowEntitySubscription>(Subscriptions.workflow.event, {
    onData({ data: subscriptionData }: OnDataOptions<API.WorkflowEntitySubscription>) {
      if (isNil(subscriptionData.data)) {
        return;
      }
      const { type, entities } = subscriptionData.data.event;
      if (type !== 'WORKFLOW_ENTITY_UPDATED') {
        return;
      }

      const remoteEntity = entities.find(e => e.id === entityId);
      const components = workflowTemplate?.components;

      if (isNil(remoteEntity) || isNil(components) || isNil(originalFields)) {
        return;
      }

      const remoteFieldRecord = readRemoteFieldRecord({
        client,
        entityId,
        components,
        remoteEntity,
        currentUser: profile,
      });
      if (isNil(remoteFieldRecord)) {
        return;
      }

      const remotelyChangedIds = getRemotelyChangedIds(remoteFieldRecord, originalFields);
      if (remotelyChangedIds.length === 0) {
        return;
      }

      if (!isDirty) {
        setFields(remoteFieldRecord);
        setOriginalFields(remoteFieldRecord);
        setConflictingComponentIds([]);
        return;
      }

      const locallyModifiedIds = new Set(Object.keys(modifiedFields ?? {}));
      const { safeToUpdateIds, ownSaveIds, newConflictIds } = classifyRemoteChanges({
        remotelyChangedIds,
        locallyModifiedIds,
        localFields: fields,
        remoteFieldRecord,
        pendingSaveComponentIds: pendingSaveComponentIdsRef.current,
      });

      if (safeToUpdateIds.length > 0) {
        setFields(prev =>
          patchRecord({ base: prev, ids: safeToUpdateIds, source: remoteFieldRecord }),
        );
        setOriginalFields(prev =>
          isNil(prev)
            ? prev
            : patchRecord({ base: prev, ids: safeToUpdateIds, source: remoteFieldRecord }),
        );
      }

      if (ownSaveIds.length > 0) {
        // Advance the baseline only. Leave `fields` untouched in case the user
        // kept editing while this save was still in flight.
        setOriginalFields(prev =>
          isNil(prev)
            ? prev
            : patchRecord({ base: prev, ids: ownSaveIds, source: remoteFieldRecord }),
        );
      }

      if (newConflictIds.length > 0) {
        setConflictingComponentIds(prev => [...new Set([...prev, ...newConflictIds])]);
        setConflictingRemoteFields(prev =>
          patchRecord({ base: prev, ids: newConflictIds, source: remoteFieldRecord }),
        );
      }
    },
  });

  const updateField = useCallback(
    (componentId: string, field: Field | null | GetUpdatedField, skipEqualityCheck = false) => {
      setFields(previousFields => {
        const previousField = previousFields[componentId];

        let updatedField: Field | null;
        if (typeof field === 'function') {
          updatedField = field(previousField);
        } else {
          updatedField = field;
        }

        if (skipEqualityCheck || isFieldModified(previousField, updatedField)) {
          return { ...previousFields, [componentId]: updatedField };
        } else {
          return previousFields;
        }
      });
    },
    [],
  );

  const resetDirty = useCallback(() => setOriginalFields(fields), [fields]);

  // Clear conflict markers once the form has no local edits (after save or initial load).
  useEffect(() => {
    if (!isDirty) {
      setConflictingComponentIds([]);
      setConflictingRemoteFields({});
    }
  }, [isDirty]);

  const revertConflicts = useCallback(() => {
    setFields(prev => ({ ...prev, ...conflictingRemoteFields }));
    setOriginalFields(prev => (isNil(prev) ? prev : { ...prev, ...conflictingRemoteFields }));
    setConflictingComponentIds([]);
    setConflictingRemoteFields({});
  }, [conflictingRemoteFields]);

  useEffect(() => {
    const entity = data?.workflowEntity?.entity;
    const refs = data?.workflowEntity?.refs;
    const components = workflowTemplate?.components;
    const fieldsHaveBeenSet = !isEmpty(fields) || !isEmpty(originalFields);
    if (entity && refs && components && !fieldsHaveBeenSet) {
      const fieldRecord = buildFieldRecord({
        entity,
        components,
        refs,
        currentUser: profile,
      });
      setOriginalFields(fieldRecord);
      setFields(fieldRecord);
    }
  }, [data, fields, originalFields, workflowTemplate?.components, profile]);

  return {
    fields,
    rawFields: data?.workflowEntity?.entity.fields,
    originalFields,
    modifiedFields,
    threadId: data?.workflowEntity?.entity.threadId,
    updateField,
    loading,
    isDirty,
    resetDirty,
    conflictingComponentIds,
    revertConflicts,
    markComponentsPendingSave,
    clearComponentsPendingSave,
    errors: !isNil(error) ? [{ description: error.message }] : [],
  };
}

/**
 * Reads the Apollo cache for the entity's refs (correctly typed from
 * GetWorkflowEntityQuery) and builds a FieldRecord from the incoming
 * subscription entity. Returns null if the cache entry is missing.
 */
function readRemoteFieldRecord({
  client,
  entityId,
  components,
  remoteEntity,
  currentUser,
}: {
  client: ApolloClient<object>;
  entityId: string;
  components: API.AnyComponent[];
  remoteEntity: API.WorkflowEntity;
  currentUser: User | undefined;
}): FieldRecord | null {
  const cachedEntity = client.readQuery<
    API.GetWorkflowEntityQuery,
    API.GetWorkflowEntityQueryVariables
  >({
    query: Queries.workflow.getWorkflowEntity,
    variables: { input: { id: entityId } },
  });
  const cachedRefs = cachedEntity?.workflowEntity?.refs;
  if (isNil(cachedRefs)) {
    return null;
  }
  return buildFieldRecord({ entity: remoteEntity, components, refs: cachedRefs, currentUser });
}

/** Returns the component IDs whose remote value differs from the last-known original. */
function getRemotelyChangedIds(
  remoteFieldRecord: FieldRecord,
  originalFields: FieldRecord,
): string[] {
  return Object.keys(remoteFieldRecord).filter(id =>
    isFieldModified(originalFields[id], remoteFieldRecord[id]),
  );
}

/**
 * Splits remotely-changed IDs into three buckets:
 * - safeToUpdateIds: not locally modified, or locally modified to the same value as remote
 * - ownSaveIds: locally modified to a different value than remote, but a save for this field
 *   is currently in flight, so this is our own change echoing back via the subscription
 *   before the mutation response has arrived — not a real remote conflict.
 * - newConflictIds: locally modified to a different value than remote, and not one of our
 *   own pending saves
 */
export function classifyRemoteChanges({
  remotelyChangedIds,
  locallyModifiedIds,
  localFields,
  remoteFieldRecord,
  pendingSaveComponentIds,
}: {
  remotelyChangedIds: string[];
  locallyModifiedIds: Set<string>;
  localFields: FieldRecord;
  remoteFieldRecord: FieldRecord;
  pendingSaveComponentIds: Set<string>;
}): { safeToUpdateIds: string[]; ownSaveIds: string[]; newConflictIds: string[] } {
  const safeToUpdateIds: string[] = [];
  const ownSaveIds: string[] = [];
  const newConflictIds: string[] = [];
  for (const id of remotelyChangedIds) {
    const isLocallyModifiedFromRemote =
      locallyModifiedIds.has(id) && isFieldModified(localFields[id], remoteFieldRecord[id]);

    if (!isLocallyModifiedFromRemote) {
      safeToUpdateIds.push(id);
    } else if (pendingSaveComponentIds.has(id)) {
      ownSaveIds.push(id);
    } else {
      newConflictIds.push(id);
    }
  }
  return { safeToUpdateIds, ownSaveIds, newConflictIds };
}

/** Returns a new FieldRecord with the given IDs patched from `source`. */
function patchRecord({
  base,
  ids,
  source,
}: {
  base: FieldRecord;
  ids: string[];
  source: FieldRecord;
}): FieldRecord {
  const next = { ...base };
  for (const id of ids) {
    next[id] = source[id];
  }
  return next;
}
